import { useCallback, useEffect, useRef } from 'react';

const isCaptchaDisplayed = (): boolean => {
  if (typeof document === 'undefined') return false;
  const captchaModal = document.getElementById('px-captcha-modal');
  const captchaContainer = document.querySelector('.px-captcha-container');
  return Boolean(captchaModal || captchaContainer);
};

type UseSmartPollingOptions = {
  /** Whether polling is active. Typically `!isWebSocketEnabledAndReady` so polling serves as a fallback. */
  isEnabled: boolean;
  /** Interval between polls in milliseconds. */
  pollIntervalMs: number;
  /** Called to fetch fresh data (e.g., React Query's `refetch`). */
  refetch: () => void;
  /** Start the polling timer. Receives `pollIntervalMs` so the caller can wire it to their polling mechanism (e.g., React Query's `refetchInterval`). */
  startPolling: (pollIntervalMs: number) => void;
  /** Stop the polling timer. */
  stopPolling: () => void;
};

/**
 * Smart polling hook that pauses when the browser tab is hidden and resumes
 * when the user returns. If the tab was hidden longer than `pollIntervalMs`,
 * it immediately refetches on return to avoid showing stale data.
 *
 * Also checks for PerimeterX CAPTCHA modals on mount and when the tab
 * regains focus, pausing polling to prevent request storms while the
 * user is solving a challenge.
 *
 * Best used as a fallback when a WebSocket connection cannot be established.
 *
 * @returns `updateLastPollTimestamp` — call after each successful refetch
 * so the visibility handler knows how stale the data is.
 *
 * @example
 * const { updateLastPollTimestamp } = useSmartPolling({
 *   isEnabled: !isWebSocketEnabledAndReady,
 *   pollIntervalMs: 60_000,
 *   refetch,
 *   startPolling: (ms) => setRefetchInterval(ms),
 *   stopPolling: () => setRefetchInterval(false),
 * });
 */
export function useSmartPolling({
  isEnabled,
  pollIntervalMs,
  refetch,
  startPolling,
  stopPolling,
}: UseSmartPollingOptions) {
  const lastPollTimestampRef = useRef<number>(0);

  // Use refs so event handlers always see the latest values without
  // needing to re-register the visibility listener on every change.
  const isEnabledRef = useRef(isEnabled);
  const pollIntervalMsRef = useRef(pollIntervalMs);
  const refetchRef = useRef(refetch);
  const startPollingRef = useRef(startPolling);
  const stopPollingRef = useRef(stopPolling);

  isEnabledRef.current = isEnabled;
  pollIntervalMsRef.current = pollIntervalMs;
  refetchRef.current = refetch;
  startPollingRef.current = startPolling;
  stopPollingRef.current = stopPolling;

  useEffect(() => {
    if (!isEnabled) return;
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (!isEnabledRef.current) return;

      if (document.visibilityState === 'hidden') {
        stopPollingRef.current();
      } else {
        // Check for CAPTCHA before resuming — a modal may have appeared while the tab was hidden
        if (isCaptchaDisplayed()) {
          stopPollingRef.current();
          return;
        }

        const currentTimestamp = Date.now();
        const timeSinceLastPoll = currentTimestamp - lastPollTimestampRef.current;

        if (timeSinceLastPoll > pollIntervalMsRef.current) {
          refetchRef.current();
          lastPollTimestampRef.current = currentTimestamp;
        }

        startPollingRef.current(pollIntervalMsRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check for CAPTCHA on initial mount and pause polling if present
    if (isCaptchaDisplayed()) {
      stopPollingRef.current();
    } else {
      startPollingRef.current(pollIntervalMsRef.current);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPollingRef.current();
    };
  }, [isEnabled]);

  const updateLastPollTimestamp = useCallback(() => {
    lastPollTimestampRef.current = Date.now();
  }, []);

  return {
    updateLastPollTimestamp,
  };
}
